// Revised UI layout for clarity and professionalism with nesting fix
import React from 'react';
import { Radio, InputNumber } from 'antd';

const availableModels = [
  'Rent-to-Own', 'Traditional Rental', 'Bank Lending', 'Rent-to-Own + Gold',
  'Rent-to-Own + Real Estate', 'Rent-to-Own + Stock',
  'Gold Investment', 'Stock Investment'
];

const fieldLabels = {
  propertyValue: 'Invested Amount in Asset ($)',
  propertyGrowthRate: 'Asset Appreciation Rate (%)',
  rentGrowthRate: 'Rent Growth Rate (%)',
  taxRate: 'Property Tax Rate (%)',
  insuranceRate: 'Insurance Rate (%)',
  maintenancePercentOfRent: 'Maintenance Cost (% of Rent)',
  vacancyRate: 'Vacancy Rate (%)',
  adminFeePercent: 'Rental Admin Fee (%)',
  rentPercent: 'Rent Portion (%)',
  equityPercent: 'Equity Build Portion (%)',
  goldStartPrice: 'Starting Gold Price ($/gram)',
  goldGrowthRate: 'Gold Appreciation Rate (%)',
  reinvestmentSplit: 'Reinvestment Split (%)',
  stockGrowthRate: 'Stock Growth Rate (%)',
  businessRevenueGrowthRate: 'Business Revenue Growth (%)',
  businessAppreciationMultiple: 'Business Appreciation Multiple (x times)',
  interestRate: 'Loan Interest Rate (%)',
  termYears: 'Investment Term (Years)',
  sellCostRate: 'Asset Sell Cost Rate (%)',
  monthlyRentRatio: 'Standard Rent Factor (1/180 Rule)',
  monthlyInvestmentAmount: 'Monthly Investment Amount'
};

const modelParamRequirements = {
  'Traditional Rental': {
    investor: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'vacancyRate', 'adminFeePercent', 'sellCostRate', 'monthlyRentRatio'],
    entrepreneur: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'vacancyRate', 'adminFeePercent', 'sellCostRate', 'monthlyRentRatio'],
  },
  'Bank Lending': {
    investor: ['interestRate', 'propertyGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent'],
    entrepreneur: ['interestRate', 'propertyGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent'],
  },
  'Rent-to-Own': {
    investor: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'rentPercent', 'equityPercent', 'sellCostRate', 'monthlyRentRatio'],
    entrepreneur: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'rentPercent', 'equityPercent', 'sellCostRate', 'monthlyRentRatio'],
  },
  'Rent-to-Own + Gold': {
    investor: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'rentPercent', 'equityPercent', 'goldStartPrice', 'goldGrowthRate', 'sellCostRate', 'monthlyRentRatio', 'reinvestmentSplit'],
    entrepreneur: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'rentPercent', 'equityPercent', 'goldStartPrice', 'goldGrowthRate', 'sellCostRate', 'monthlyRentRatio', 'reinvestmentSplit'],
  },
  'Rent-to-Own + Real Estate': {
    investor: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'rentPercent', 'equityPercent', 'reinvestmentSplit', 'sellCostRate', 'monthlyRentRatio'],
    entrepreneur: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'rentPercent', 'equityPercent', 'reinvestmentSplit', 'sellCostRate', 'monthlyRentRatio'],
  },
  'Rent-to-Own + Stock': {
    investor: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'rentPercent', 'equityPercent', 'stockGrowthRate', 'sellCostRate', 'monthlyRentRatio', 'reinvestmentSplit'],
    entrepreneur: ['propertyGrowthRate', 'rentGrowthRate', 'taxRate', 'insuranceRate', 'maintenancePercentOfRent', 'rentPercent', 'equityPercent', 'stockGrowthRate', 'sellCostRate', 'monthlyRentRatio', 'reinvestmentSplit'],
  },
  'Gold Investment': {
    investor: ['goldStartPrice', 'goldGrowthRate', 'monthlyInvestmentAmount'],
    entrepreneur: ['goldStartPrice', 'goldGrowthRate', 'monthlyInvestmentAmount'],
  },
  'Stock Investment': {
    investor: ['stockGrowthRate'],
    entrepreneur: ['stockGrowthRate'],
  },
};

const InputInline = ({ value, onChange, min = 0.001, max, step }) => {
  const handleBlur = () => {
    const numericValue = Number(value);

    if (!numericValue || isNaN(numericValue) || numericValue < min) {
      onChange(min); // Force fallback if cleared or below min
    }
  };

  return (
    <InputNumber
      value={value}
      onChange={(v) => {
        // Prevent typing 0 or negative
        if (v === null || v === '' || v < min) {
          onChange(min);
        } else {
          onChange(v);
        }
      }}
      onBlur={handleBlur}
      min={min}
      max={max}
      step={step}
      className="w-28 mx-1"
      size="small"
    />
  );
};





const IntroInputForm = ({ params, setParams, setSelectedModels, audience, setAudience, preferredModel, setPreferredModel }) => {
  const audienceKey = audience === 'Entrepreneur Explorer' ? 'entrepreneur' : 'investor';

  const modelsForAudience = audience === 'Investor'
    ? availableModels.filter(m => m !== '')
    : availableModels.filter(m => ![].includes(m));

  const selectedModelFields = modelParamRequirements[preferredModel]?.[audienceKey] || [];
  const alwaysFields = ['propertyValue', 'termYears'];
  const usedFields = [...alwaysFields, ...selectedModelFields];

  const allPossibleFields = Array.from(new Set(Object.values(modelParamRequirements).flatMap(m => m[audienceKey] || [])));
  const remainingFields = allPossibleFields.filter(f => !usedFields.includes(f));

  const buildSentence = (fields) => fields.map((field, idx) => (
    <span key={field}>
      {idx > 0 && (idx === fields.length - 1 ? ' and ' : ', ')}
      {fieldLabels[field]}: <InputInline
        value={params[field]}
        onChange={(v) => setParams({ ...params, [field]: v })}
        min={0}
        step={field.includes('Rate') || field.includes('Percent') ? 0.1 : 1}
      />
    </span>
  ));

  return (
    <div className="bg-white p-6 md:p-10 rounded-lg shadow-md max-w-5xl mx-auto text-gray-800 space-y-6">
      {/* <h2 className="text-xl font-semibold text-gray-700">
    Regimes rise and fall. Currencies come and go. But gold never leaves the throne. Real estate builds equity. Together, they don’t just grow wealth — <strong>they multiply it</strong>.
    Here, <strong>business replaces interest</strong>. And ownership isn’t inherited — it’s earned.
  </h2>
  <p>
    
  </p> */}

      <section className="space-y-6 text-base text-gray-800 leading-relaxed">

        <p className="text-lg font-semibold text-gray-700">
          An engine and simulator that strategies to <strong>outrun interest and manage inflation</strong> risk.
          Nest Quest ROI places rent, mortgage, rent-to-own, and buy-out on a single, auditable ledger. It delivers reproducible results so households, business owners, investors, and advisors can compare cash flow, equity, risk, and reinvestment side by side within locked scenarios.
        </p>
        <p className="text-lg font-semibold text-yellow-700">
          Put money to work before inflation does. Clear math. Transparent choices. Smarter paths to financial success.
        </p>



        <div className="space-y-4">
          <p>
            <strong>For <span className="text-purple-700">Housholds or Entrepreneur Explorer</span>:</strong> Should you rent, get a mortgage, or choose a rent-to-own path? See the lifetime cost, pace to ownership and outcomes before you decide.
          </p>

          <p>
            <strong>For <span className="text-blue-700">Investors & Lenders</span>:</strong> Move beyond brochures. Compare financial products on real economic performance, measure true margins (RNIM), and design resilient contracts under identical, auditable assumptions.
          </p>

          <p>
            <strong>For <span className="text-green-700">Professionals & Policymakers</span>:</strong> This is your research lab. Stress-test models, build coursework, and evaluate policy impacts on a shared, transparent platform. Every result is reproducible and peer-reviewable.
          </p>
        </div>

        <p className="text-lg font-medium text-gray-900">
          Agree with our model or outdo it. Keep what works. Improve what does not—that is innovation. Replicate results, evaluate trade-offs, and improve designs transparently. One engine. One ledger. <span className="italic">Every pathway. Shared insight. </span> Better decisions.
        </p>

        {/* <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
    <p className="text-sm text-gray-700 mb-2"><strong>Read the Book & Explore the Research</strong></p>
    <a href="https://a.co/d/0Cs0k7q" target="_blank" rel="noopener noreferrer" className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition-colors duration-200">
      Get the Book on Amazon
    </a>
  </div> */}

        {/* Collapsible “Read the Book & Explore the Research” section */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          {/* State & toggle logic */}
          {(() => {
            const [open, setOpen] = React.useState(false);
            return (
              <>
                <button
                  onClick={() => setOpen(!open)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-800 focus:outline-none"
                >
                  <span>Read the Book & Explore the Research</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-blue-600 transition-transform duration-200 ${open ? "rotate-180" : ""
                      }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {open && (
                  <div className="mt-3 space-y-3 text-sm">
                    <a
                      href="https://a.co/d/0Cs0k7q"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors duration-200"
                    >
                      Get the Book on Amazon
                    </a>

                    <p className="text-xs text-gray-600">
                      Check your local bookstore — globally available through{" "}
                      <strong>IngramSpark</strong> distribution.
                    </p>

                    <div>
                      <p className="text-sm font-semibold text-gray-800 mb-1">
                        Published Research on SSRN:
                      </p>
                      <ul className="list-disc pl-4 text-blue-700 space-y-0.5">
                        <li>
                          <a
                            href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5623371"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            DGSA Framework: Digital Gold Savings Account
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5563858"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            RTO+Gold: Empirical Analysis of Asset-Backed Rent-to-Own
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5707685"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            RTO Framework: Policy-Aligned Model for Equitable Homeownership
                          </a>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-800 mb-1">
                        Explore Our Platforms:
                      </p>
                      <ul className="list-disc pl-4 text-blue-700 space-y-0.5">
                        <li>
                          <a
                            href="https://marketplace.microsoft.com/en-us/product/saas/softtelrgllc1622855179214.nestquestroi"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            NestQuestROI on Microsoft Azure Marketplace
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://www.producthunt.com/posts/nestquestroi"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            Discover on Product Hunt
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://www.crunchbase.com/organization/softtelrg"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            Company Profile on Crunchbase
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://www.dealroom.co/companies/softtelrg"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            Company Profile on Dealroom
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://www.linkedin.com/company/softtelrg-llc"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            Follow us on LinkedIn
                          </a>
                        </li>

                      </ul>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

      </section>

      <section className="mt-8 space-y-4 text-base text-gray-800 leading-relaxed">
        {/* <h3 className="text-lg font-semibold text-gray-900">
    Are you an <span className="text-blue-700">Investor</span> focused on growing wealth through touchable assets and real business participation,  
    or an <span className="text-purple-700">Entrepreneur Explorer</span> planning to own a home, launch a business, or build something meaningful through committed effort?
  </h3> */}

        <p className="text-lg font-bold text-green-700">
          Let the math decide.
        </p>
      </section>



      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-700">Select Your Starting Model</h3>
        <div className="flex flex-wrap gap-2">
          {modelsForAudience.map(model => (
            <Radio.Button
              key={model}
              value={model}
              checked={preferredModel === model}
              onChange={(e) => {
                setPreferredModel(e.target.value);
                if (setSelectedModels) {
                  const fullModels = audience === 'Investor'
                    ? ['Bank Lending', 'Traditional Rental', 'Rent-to-Own', 'Rent-to-Own + Gold', 'Rent-to-Own + Real Estate', 'Rent-to-Own + Stock', 'Gold Investment', 'Stock Investment']
                    : ['Bank Lending', 'Traditional Rental', 'Rent-to-Own', 'Rent-to-Own + Gold', 'Rent-to-Own + Real Estate', 'Rent-to-Own + Stock', 'Gold Investment', 'Stock Investment'];
                  setSelectedModels(fullModels);
                }
              }}
            >
              {model}
            </Radio.Button>
          ))}
        </div>
      </section>

      {preferredModel && (
        <section className="bg-gray-50 p-4 rounded-md space-y-2">
          {/* <h3 className="text-md font-semibold text-gray-700">3. Set Your Assumptions</h3> */}
          <div className="leading-relaxed flex flex-wrap items-center gap-1">
            <span><strong>{fieldLabels['propertyValue']}</strong>:</span>
            <InputInline value={params.propertyValue} onChange={(v) => setParams({ ...params, propertyValue: v })} min={1} step={1000} />
            <span><strong>{fieldLabels['termYears']}</strong>:</span>
            <InputInline value={params.termYears} onChange={(v) => setParams({ ...params, termYears: v })} min={1} max={40} step={1} />
            <span>This <strong>{preferredModel}</strong> strategy assumes</span>
            {buildSentence(selectedModelFields)}.
          </div>
        </section>
      )}

      {preferredModel && remainingFields.length > 0 && (
        <section className="bg-gray-50 p-4 rounded-md space-y-2">
          <h4 className="text-md font-semibold text-gray-700">Additional Comparative Inputs</h4>
          <div className="leading-relaxed flex flex-wrap items-center gap-1">
            <span>We also track:</span>
            {buildSentence(remainingFields)}.
          </div>
        </section>
      )}
    </div>
  );
};

export default IntroInputForm;
